import { getCachedViewSnapshot } from '@databases/database-state/state/view';
import cellSelectionsSlice from '@databases/redux/cellSelections/slice';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { appStore } from '@databases/redux/store';
import {
  initializeSelectionsMachine,
  type SelectionsMachine,
} from '@databases/selections';
import {
  type DatabaseAdapter,
  LegacyReduxAdapter,
  ReduxDatabaseAdapter,
  SelectionsApi,
  SelectionsApiAdapter,
} from '@databases/selections-api';
import { container } from '@databases/service-container';

import { getExperimentValue } from '@canvas/client-feature-flags';

function registerLegacySelectionsActions(contentId: string) {
  container.registerSingleton(
    'SelectionsActions',
    () =>
      new LegacyReduxAdapter({
        contentId,
        dispatch: appStore.dispatch,
        sliceActions: cellSelectionsSlice.actions,
      })
  );
}

/**
 * Initializes the selections machine behind a feature gate
 * with a listener on databaseContentIdChanged to destroy
 * and rebuild it, so no highlights or state exists between
 * changing the content id.
 */
export function initializeRecyclableSelectionsMachine(initialContentId: string) {
  const experimentIsEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    {
      fireExperimentExposure: false,
    }
  );

  if (!experimentIsEnabled) {
    registerLegacySelectionsActions(initialContentId);

    const eventBus = container.resolve('EventBus');

    eventBus.on('databaseContentIdChanged', (data) => {
      registerLegacySelectionsActions(data.contentId);
    });

    return;
  }

  let machine: SelectionsMachine;
  let destroy: VoidFunction;
  let selectionsApi: SelectionsApi;
  let databaseAdapter: DatabaseAdapter;

  function setup(currentContentId: string) {
    ({ machine, destroy } = initializeSelectionsMachine());

    databaseAdapter = new ReduxDatabaseAdapter({
      getContentId: () => currentContentId,
      getDatabaseSnapshot: (id) =>
        selectDatabaseDraftByContentId(appStore.getState(), {
          contentId: id,
          includeDeleted: false,
          viewSettings: getCachedViewSnapshot().settings,
        }),
    });

    selectionsApi = new SelectionsApi({ machine, adapter: databaseAdapter });

    container.registerSingleton('SelectionsActions', () => {
      return new SelectionsApiAdapter({
        selectionsApi,
      });
    });
  }

  setup(initialContentId);

  const rebuildSelectionsMachineState = (data: { contentId: string }) => {
    destroy();

    setup(data.contentId);
  };

  const eventBus = container.resolve('EventBus');

  eventBus.on('databaseContentIdChanged', rebuildSelectionsMachineState);
}
